import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook that simulates a human-like typing effect.
 * Reveals text in random-sized word chunks with variable delays.
 * 
 * Features:
 * - Reveals 2-5 words at a time (configurable)
 * - Variable delays between chunks to simulate human rhythm
 * - Longer pauses after punctuation (periods, commas, question marks) to simulate "thinking"
 * - Starts slower, speeds up as the "person" gets into the flow
 * - Returns isTyping flag for blinking cursor
 *
 * @param {string} fullText - The complete text to "type"
 * @param {boolean} isActive - Whether the typewriter effect should be running
 * @param {Object} [options] - Configuration options
 * @param {number} [options.minWords=2] - Minimum words per chunk
 * @param {number} [options.maxWords=5] - Maximum words per chunk
 * @param {number} [options.minDelay=30] - Minimum delay between chunks in ms
 * @param {number} [options.maxDelay=80] - Maximum delay between chunks in ms
 * @param {number} [options.punctuationDelay=180] - Extra delay after punctuation (., ?, !)
 * @param {number} [options.commaDelay=100] - Extra delay after commas
 * @param {number} [options.initialSlowdown=2] - Multiplier for the first few chunks (slower start)
 * @param {number} [options.slowdownChunks=3] - How many initial chunks are slowed down
 * @returns {{ displayedText: string, isTyping: boolean }}
 */
export const useTypewriterEffect = (fullText, isActive, options = {}) => {
  const {
    minWords = 2,
    maxWords = 5,
    minDelay = 30,
    maxDelay = 80,
    punctuationDelay = 180,
    commaDelay = 100,
    initialSlowdown = 2,
    slowdownChunks = 3,
  } = options;

  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef(null);
  const currentIndexRef = useRef(0);
  const chunkCountRef = useRef(0);
  const prevTextRef = useRef('');
  const isActiveRef = useRef(isActive);

  // Keep isActive ref in sync
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  const getRandomInt = useCallback((min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }, []);

  /**
   * Checks if the last word in a chunk ends with punctuation
   * and returns the appropriate extra delay.
   */
  const getPunctuationDelay = useCallback((chunk) => {
    if (!chunk) return 0;
    const lastChar = chunk.trim().slice(-1);
    
    // Full stop, question mark, exclamation - longer pause (thinking)
    if (['.', '?', '!'].includes(lastChar)) return punctuationDelay;
    // Comma, semicolon, colon - shorter pause
    if ([',', ';', ':'].includes(lastChar)) return commaDelay;
    
    return 0;
  }, [punctuationDelay, commaDelay]);

  useEffect(() => {
    if (!fullText) {
      setDisplayedText('');
      setIsTyping(false);
      return;
    }

    // Split into words
    const words = fullText.split(' ');
    const prevText = prevTextRef.current || '';
    const currentWordIndex = currentIndexRef.current;

    // History message (not streaming and never started) - show immediately
    if (!isActive && currentWordIndex === 0 && !prevText) {
      setDisplayedText(fullText);
      prevTextRef.current = fullText;
      currentIndexRef.current = words.length;
      setIsTyping(false);
      return;
    }

    // Update prevText reference (for next comparison)
    prevTextRef.current = fullText;

    // KEY FIX: When text changes during streaming, DON'T reset displayedText
    // Just continue typing from where we are. The backend sends replacement text,
    // but we don't want visual flashing - keep current position and type new words.
    // Only adjust if our position exceeds new word count.
    if (currentWordIndex > words.length) {
      currentIndexRef.current = words.length;
    }

    // If all words are already revealed, show full text
    if (currentIndexRef.current >= words.length) {
      setDisplayedText(fullText);
      if (!isActive) {
        setIsTyping(false);
      }
      return;
    }

    setIsTyping(true);

    const typeNextChunk = () => {
      const currentIndex = currentIndexRef.current;

      if (currentIndex >= words.length) {
        // All words revealed for now
        setDisplayedText(fullText);
        // Only signal "done typing" if streaming is also finished;
        // while still streaming, more words may arrive.
        if (!isActiveRef.current) {
          setIsTyping(false);
        }
        return;
      }

      // Pick a random chunk size
      const chunkSize = getRandomInt(minWords, maxWords);
      const endIndex = Math.min(currentIndex + chunkSize, words.length);
      const newChunk = words.slice(currentIndex, endIndex).join(' ');

      currentIndexRef.current = endIndex;
      chunkCountRef.current += 1;

      // Build displayed text up to current position
      const revealed = words.slice(0, endIndex).join(' ');
      setDisplayedText(revealed);

      // Calculate delay
      let delay = getRandomInt(minDelay, maxDelay);

      // Slowdown for the first few chunks (start slower, then speed up)
      if (chunkCountRef.current <= slowdownChunks) {
        const slowFactor = initialSlowdown - ((chunkCountRef.current - 1) * (initialSlowdown - 1) / slowdownChunks);
        delay = Math.round(delay * slowFactor);
      }

      // Add extra delay after punctuation
      delay += getPunctuationDelay(newChunk);

      // Schedule next chunk if there are more words
      if (endIndex < words.length) {
        timeoutRef.current = setTimeout(typeNextChunk, delay);
      } else {
        // Finished revealing all current words
        setDisplayedText(fullText);
        // Only signal "done typing" if streaming is finished;
        // while still streaming, keep isTyping=true so indicators stay visible.
        if (!isActiveRef.current) {
          setIsTyping(false);
        }
      }
    };

    // Start typing from current position
    if (currentIndexRef.current < words.length) {
      timeoutRef.current = setTimeout(typeNextChunk, getRandomInt(minDelay, maxDelay));
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [fullText, isActive, minWords, maxWords, minDelay, maxDelay, initialSlowdown, slowdownChunks, getRandomInt, getPunctuationDelay]);

  // For history messages (not streaming), return full text immediately to avoid flash of empty
  // This handles the case before the effect runs on first render
  const finalDisplayedText = (!isActive && displayedText === '' && fullText) ? fullText : displayedText;

  return { displayedText: finalDisplayedText, isTyping };
};
